import { useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import type { Action } from 'ui/components/ViiluFullPageDialog'
import ViiluFullPageDialog from 'ui/components/ViiluFullPageDialog'
import { StandardDialogContent } from 'ui/StyledComponents/BasicComponents'
import type { MeterInvoicingDraft } from './types'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import styled from 'styled-components'
import BasicInformationForm from './BasicInformationForm'
import PaymentProductsForm from './PaymentProductsForm'
import usePaymentProducts from 'ui/ManagerFinance/PaymentProducts/usePaymentProducts'
import ContractsForm from './ContractsForm'
import { useSelectedManagerCompanies } from 'util/hooks/useSelectedManagerCompanies'
import { createManagerCompanyOptions } from 'ui/ManagerFinance/utils/managerCompanyOptions'
import { useBuildings } from 'ui/CompanyBuildings/hooks'
import { useCompanyApartments } from 'util/hooks/useCompanyApartments'
import Stepper from 'ui/MessageService/MessageServiceProcess/Stepper'
import InvoiceForm from './InvoiceForm'
import {
	_createMeterInvoiceRows,
	_createMeterInvoices,
} from 'state/finance-invoice-actions'
import * as notifyActions from 'state/notifyActions'
import { useDispatch } from 'react-redux'
import { GENERIC_ERROR_NOTIFICATION } from 'ui/messages'
import {
	meterInvoicingBasicInformationFormSchema,
	meterInvoicingContractFormSchema,
	meterInvoicingPaymentProductFormSchema,
} from './validation'
import { useZodValidationResolver } from 'util/hooks/useZodValidationResolver'
import { meterInvoicingDefaultValues } from './constants'
import type { z } from 'zod'

const Container = styled.section`
	width: 100%;
`

type Props = {
	onHide: () => void
}

const MeterInvoiceDialog = ({ onHide }: Props) => {
	const intl = useIntl()
	const dispatch = useDispatch()

	const [processing, setProcessing] = useState(false)

	const selectedFinanceCompanyUUID = useSelector(
		({ app }: any) => app?.selectedFinanceCompanyUUID,
	)

	const { selectedManagerCompanies } = useSelectedManagerCompanies()
	const companyOptions = createManagerCompanyOptions(selectedManagerCompanies)

	const { buildings } = useBuildings(selectedFinanceCompanyUUID)
	const { companyApartments } = useCompanyApartments(selectedFinanceCompanyUUID)

	const filters = useMemo(
		() => ({
			meterBilling: true,
		}),
		[],
	)

	const { data: paymentProducts } = usePaymentProducts(
		selectedFinanceCompanyUUID,
		filters,
	)

	const formSteps: {
		id: string
		label: string
		validation?: z.ZodObject<any>
	}[] = [
		{
			id: 'step-basic-information',
			label: intl.formatMessage({
				defaultMessage: 'Perustiedot',
				description: 'Basic info step label',
			}),
			validation: meterInvoicingBasicInformationFormSchema(intl),
		},
		{
			id: 'step-payment-products',
			label: intl.formatMessage({
				defaultMessage: 'Maksulajit',
				description: 'Payment products step label',
			}),
			validation: meterInvoicingPaymentProductFormSchema(intl),
		},
		{
			id: 'step-contracts',
			label: intl.formatMessage({
				defaultMessage: 'Sopimusten valinta',
				description: 'Select contracts step label',
			}),
			validation: meterInvoicingContractFormSchema(intl),
		},
		{
			id: 'step-invoice-rows',
			label: intl.formatMessage({
				defaultMessage: 'Laskurivit',
				description: 'Invoice lines step label',
			}),
		},
	]

	const [step, setStep] = useState(0)
	const [reachedStepIndex, setReachedStepIndex] = useState(0)

	const currentFormStep = formSteps[step]

	const currentStepFormSchema = currentFormStep.validation

	const resolver = useZodValidationResolver<MeterInvoicingDraft>(
		currentStepFormSchema,
	)

	const formMethods = useForm<MeterInvoicingDraft>({
		resolver,
		defaultValues: {
			...meterInvoicingDefaultValues,
			companyUUID: selectedFinanceCompanyUUID,
		},
		mode: 'onBlur',
		shouldUnregister: false,
	})

	const [contractUUIDs, paymentProductUUIDs, meterInvoicingRowsByContract] =
		useWatch({
			control: formMethods.control,
			name: [
				'contractUUIDs',
				'paymentProductUUIDs',
				'meterInvoicingRowsByContract',
			],
		})

	const getInvoiceRows = async () => {
		try {
			const currentData = formMethods.getValues()
			const response: Response | undefined = await _createMeterInvoiceRows({
				companyUUID: currentData.companyUUID,
				description: currentData.description,
				invoiceDate: currentData.invoiceDate,
				dueDate: currentData.dueDate,
				invoicePeriodStartDate: currentData.invoicePeriodStartDate,
				invoicePeriodEndDate: currentData.invoicePeriodEndDate,
				additionalInformation: currentData.additionalInformation,
				paymentProductUUIDs: currentData.paymentProductUUIDs,
				contractUUIDs: currentData.contractUUIDs,
			})

			if (response?.ok) {
				const result = await response.json()
				formMethods.reset(result.meterInvoiceDraft)
			}
		} catch (error) {
			console.error('Error fetching invoice rows:', error)
		}
	}

	const renderContent = () => {
		return (
			<Container>
				<h2
					className="md-title"
					style={{ marginBottom: '0', padding: '1rem 1.5rem' }}
				>
					{currentFormStep.label}

					<hr style={{ borderColor: 'rgba(0, 0, 0, 0.2)' }} />
				</h2>
				<FormProvider {...formMethods}>
					{step === 0 && <BasicInformationForm />}
					{step === 1 && (
						<PaymentProductsForm paymentProducts={paymentProducts} />
					)}
					{step === 2 && (
						<ContractsForm
							companyOptions={companyOptions}
							buildings={buildings}
							companyApartments={companyApartments}
						/>
					)}
					{step === 3 && <InvoiceForm companyApartments={companyApartments} />}
				</FormProvider>
			</Container>
		)
	}

	const onSubmit = async () => {
		setProcessing(true)

		try {
			const currentData = formMethods.getValues()
			const result = await _createMeterInvoices(currentData)

			if (!result || result.ok === false) {
				dispatch(notifyActions.error(GENERIC_ERROR_NOTIFICATION(intl)))
				setProcessing(false)
				return
			}

			dispatch(
				notifyActions.success(
					intl.formatMessage({
						defaultMessage: 'Tasauslaskut on luotu.',
						description: 'Notification text of meter invoices has been created',
					}),
				),
			)
			onHide()
		} catch (error) {
			alert('Something went wrong.')
			setProcessing(false)
		}
	}

	const moveToStep = async (stepIndex: number) => {
		if (formSteps[stepIndex].id === 'step-invoice-rows') {
			getInvoiceRows()
		}

		if (stepIndex > step) {
			const isStepValid = await formMethods.trigger()
			if (isStepValid) {
				setStep(stepIndex)
				if (reachedStepIndex < stepIndex) {
					setReachedStepIndex(stepIndex)
				}
			}
		} else {
			setStep(stepIndex)
		}
	}

	const renderActions = () => {
		const disableNextButton =
			(currentFormStep.id === 'step-contracts' && !contractUUIDs.length) ||
			(currentFormStep.id === 'step-payment-products' &&
				!paymentProductUUIDs.length) ||
			(currentFormStep.id === 'step-invoice-rows' &&
				!meterInvoicingRowsByContract.some((m) => m.invoiceRows.length))

		const actions: Array<Action | undefined> = [
			step > 0
				? {
						id: 'back-button',
						onClick: () => moveToStep(step > 0 ? step - 1 : 0),
						align: 'right',
						children: (
							<FormattedMessage
								defaultMessage="Takaisin"
								description="Back button"
							/>
						),
				  }
				: undefined,
			step !== formSteps.length - 1
				? {
						id: 'next-button',
						raised: true,
						secondary: true,
						align: 'right',
						disabled: disableNextButton,
						onClick: () =>
							moveToStep(step < formSteps.length - 1 ? step + 1 : step),
						children: (
							<FormattedMessage
								defaultMessage="Seuraava"
								description="Next step button"
							/>
						),
				  }
				: {
						id: 'create-button',
						raised: true,
						secondary: true,
						align: 'right',
						onClick: onSubmit,
						disabled: processing || disableNextButton,
						children: (
							<FormattedMessage
								defaultMessage="Luo tasauslaskut"
								description="Create meter invoices button"
							/>
						),
				  },
		]
		const filteredActions = actions.filter(
			(action): action is Action => action !== undefined,
		)
		return filteredActions
	}

	const title = intl.formatMessage({
		defaultMessage: 'Luo tasauslaskut',
		description: 'Title for the meter invoice dialog: Create meter invoices',
	})

	return (
		<ViiluFullPageDialog
			id="meter-invoice-dialog"
			title={title}
			visible
			actions={renderActions()}
			focusOnMount={false}
			toolbarStyle={{
				background: 'var(--color-secondary-dark)',
			}}
			onHide={onHide}
			navActions={
				<Stepper
					steps={formSteps}
					goToStep={({ id }) =>
						moveToStep(formSteps.findIndex((step) => step.id === id) || 0)
					}
					activeStepIndex={formSteps[step]}
					reachedStepIndex={reachedStepIndex}
				/>
			}
		>
			<StandardDialogContent>{renderContent()}</StandardDialogContent>
		</ViiluFullPageDialog>
	)
}

export default MeterInvoiceDialog
